<template>
  <div class="help-floating">
    <transition name="help-panel">
      <section
        v-if="isOpen"
        class="help-panel"
      >
        <div class="help-panel__header">
          <div>
            <span class="help-panel__eyebrow">
              <feather-icon
                icon="ZapIcon"
                size="11"
              />
              GeoProc Assistant
            </span>
            <h2>Feature Guide</h2>
            <p>How each module flows from request to finish, role by role.</p>
          </div>
          <button
            type="button"
            class="help-icon-btn"
            aria-label="Close help"
            @click="isOpen = false"
          >
            <feather-icon
              icon="XIcon"
              size="18"
            />
          </button>
        </div>

        <div
          class="help-search"
          :class="{ 'help-search--focused': isSearchFocused }"
        >
          <span class="help-search__icon">
            <feather-icon
              icon="SearchIcon"
              size="16"
            />
          </span>
          <input
            ref="searchInput"
            v-model="search"
            type="text"
            class="help-search__input"
            placeholder="Ask or search a feature, role, or step..."
            autocomplete="off"
            @focus="isSearchFocused = true"
            @blur="isSearchFocused = false"
          >
          <button
            v-if="search"
            type="button"
            class="help-search__clear"
            aria-label="Clear search"
            @mousedown.prevent="clearSearch"
          >
            <feather-icon
              icon="XIcon"
              size="14"
            />
          </button>
          <span
            v-else
            class="help-search__spark"
          >
            <feather-icon
              icon="ZapIcon"
              size="13"
            />
          </span>
        </div>

        <div class="help-tabs">
          <button
            v-for="category in categories"
            :key="category"
            type="button"
            :class="{ active: activeCategory === category }"
            @click="activeCategory = category"
          >
            {{ category }}
          </button>
        </div>

        <div class="help-list">
          <article
            v-for="item in filteredGuides"
            :key="item.title"
            class="help-card"
          >
            <div class="help-card__title">
              <span>
                <feather-icon :icon="item.icon" />
              </span>
              <div>
                <h3>{{ item.title }}</h3>
                <p>{{ item.summary }}</p>
              </div>
            </div>

            <div
              v-if="item.flow && item.flow.length"
              class="help-flow"
            >
              <div class="help-flow__track">
                <template v-for="(role, index) in item.flow">
                  <span
                    :key="role"
                    class="help-flow__node"
                    :class="{ 'is-finish': index === item.flow.length - 1 }"
                  >
                    {{ role }}
                  </span>
                  <span
                    v-if="index < item.flow.length - 1"
                    :key="`arrow-${role}`"
                    class="help-flow__arrow"
                    aria-hidden="true"
                  >&rarr;</span>
                </template>
              </div>
              <p
                v-if="item.flowNote"
                class="help-flow__note"
              >
                {{ item.flowNote }}
              </p>
            </div>

            <ol class="help-card__steps">
              <li
                v-for="step in item.steps"
                :key="step"
              >
                {{ step }}
              </li>
            </ol>

            <div class="help-card__meta">
              <span
                v-for="tag in item.tags"
                :key="tag"
              >
                {{ tag }}
              </span>
            </div>
          </article>

          <div
            v-if="!filteredGuides.length"
            class="help-empty"
          >
            <feather-icon
              icon="SearchIcon"
              size="22"
            />
            <strong>No guide found</strong>
            <span>Try another keyword like PO, receive, payment, transfer, asset, or stock.</span>
          </div>
        </div>
      </section>
    </transition>

    <button
      type="button"
      class="help-trigger"
      :aria-label="isOpen ? 'Close help' : 'Open help'"
      @click="isOpen = !isOpen"
    >
      <transition
        name="help-trigger-icon"
        mode="out-in"
      >
        <feather-icon
          v-if="isOpen"
          key="close"
          icon="XIcon"
          size="20"
        />
        <img
          v-else
          key="logo"
          src="@/assets/images/logo/geolim.png"
          alt="GeoLIMS Help"
        >
      </transition>
    </button>
  </div>
</template>

<script>

// Every `flow` below is the verified role sequence a request actually
// travels through, read straight off the confirm-chain code (the same
// `purchase_request_is_confirmed_by_*` / timeline-building logic used by
// each module's own Details.vue and the backend's confirm* controllers) -
// not a guess at "how it probably works". Where a module branches (branch
// vs head office, category, amount threshold, etc.) the longest/most
// representative chain is shown, with `flowNote` calling out the shorter
// variant so the diagram doesn't overstate what a given request needs.
const guides = [
  {
    category: 'Procurement',
    title: 'Material Request',
    icon: 'ClipboardIcon',
    summary: 'Request operational items from warehouse and track supervisor, warehouse, send, and receive status.',
    flow: ['Requester', 'Supervisor', 'Warehouse', 'Received'],
    flowNote: 'Head office requests skip the direct warehouse send and instead feed into the Purchase Request → Purchase Order flow.',
    steps: [
      'Create a request with needed items, quantities, reason, and destination.',
      'Supervisor reviews the request, then warehouse validates availability.',
      'Warehouse sends items and the requester confirms received quantity.',
      'Use list status filters to find pending, sent, received, or voided requests.',
    ],
    tags: ['MR', 'material request', 'warehouse', 'receive'],
  },
  {
    category: 'Procurement',
    title: 'Purchase Request',
    icon: 'FileTextIcon',
    summary: 'Raise purchasing demand for local, head office, asset, FGS, or supplies procurement.',
    flow: ['Requester', 'Lab Manager', 'Regional Manager', 'Division Head', 'FA Coal Manager', 'Operation Director', 'Vice President', 'President Director'],
    flowNote: 'Shown for Head Office requests. Branch (cabang) requests start with Purchasing Cabang instead, and branch Supplies requests can finish right after Lab Manager.',
    steps: [
      'Create PR from requested items, category, branch, project, cost center, and need date.',
      'Approvals move by role such as lab manager, regional manager, division head, FA coal manager, directors, and procurement pusat.',
      'Rejected or revised PRs return to editable states before final approval.',
      'Approved PRs become source documents for Purchase Orders.',
    ],
    tags: ['PR', 'purchase request', 'approval', 'revise'],
  },
  {
    category: 'Procurement',
    title: 'Purchase Order',
    icon: 'ShoppingCartIcon',
    summary: 'Issue supplier orders from approved PRs and control approval, finance, receiving, and completion.',
    flow: ['Requester', 'Lab Manager', 'Regional Manager', 'Division Head', 'FA Coal Manager', 'Operation Director', 'Vice President', 'President Director', 'Purchasing (IR)', 'Warehouse (WR)'],
    flowNote: 'Shown for Head Office. Branch (cabang) POs only need Lab Manager confirmation before moving straight to receiving.',
    steps: [
      'Create PO with supplier, item prices, discounts, tax, transport, delivery schedule, and delivery address.',
      'Confirm PO through the configured approval chain.',
      'Purchasing receive creates an IR number for supplier delivery verification.',
      'Warehouse receive creates a WR number and completes stock-side receiving.',
    ],
    tags: ['PO', 'purchase order', 'IR', 'WR', 'supplier'],
  },
  {
    category: 'Finance',
    title: 'Payment Request',
    icon: 'DollarSignIcon',
    summary: 'Request payment for procurement or head office needs and follow finance approval tiers.',
    flow: ['Requester', 'Lab Manager', 'Regional Manager', 'Division Head', 'FA Coal Manager', 'Operation Director', 'Vice President', 'President Director'],
    flowNote: 'Branch requests under the configured amount limit finish right after FA Coal Manager. Head Office requests, or anything over the limit, continue through the directors.',
    steps: [
      'Create payment request with project, supplier, bank, currency, due date, details, and notes.',
      'Branch or head office type controls which approval levels are required.',
      'Lab manager, area or regional manager, division head, FA coal manager, and directors approve in sequence.',
      'Finished payment requests are available for detail review and PDF download.',
    ],
    tags: ['payment request', 'finance', 'invoice', 'approval'],
  },
  {
    category: 'Warehouse',
    title: 'Transfers',
    icon: 'CornerUpRightIcon',
    summary: 'Move asset or non-asset stock between sender and receiver branches.',
    flow: ['Requester', 'Lab Manager (Receiver)', 'Regional Manager', 'Division Head', 'FA Coal Manager', 'Lab Manager (Sender)', 'Warehouse Sender', 'Warehouse Receiver'],
    steps: [
      'Create either asset transfer or item transfer with sender, receiver, quantities, and notes.',
      'Sender-side approval and warehouse sender confirmation prepare the transfer.',
      'Receiver-side approval and warehouse receiver confirmation close the movement.',
      'Use the transfer receipt PDF for asset and non-asset transfer documentation.',
    ],
    tags: ['transfer', 'asset transfer', 'stock transfer', 'sender', 'receiver'],
  },
  {
    category: 'Warehouse',
    title: 'Receive Assets',
    icon: 'ArrowDownCircleIcon',
    summary: 'Receive asset procurement into the asset register and attach supporting documents or photos.',
    flow: ['PO Approval', 'Purchasing (IR)', 'Warehouse (WR)', 'Finance', 'Lab Manager'],
    steps: [
      'Open Receive Assets and review incoming asset documents.',
      'Validate asset identity, responsible person, branch, category, condition, and photo or document attachments.',
      'Confirm receive so asset records become available in the asset list.',
      'Use asset detail PDF for fixed asset documentation.',
    ],
    tags: ['receive assets', 'asset', 'photo', 'document'],
  },
  {
    category: 'Inventory',
    title: 'Assets',
    icon: 'HardDriveIcon',
    summary: 'Manage fixed assets, updates, disposals, histories, photos, and approval status.',
    flow: ['Requested (Finance)', 'Lab Manager', 'Regional Manager', 'Division Head', 'FA Coal Manager'],
    flowNote: 'Same chain applies to both asset updates and asset disposals.',
    steps: [
      'Use active, update, and disposal tabs to track the lifecycle of each asset.',
      'Open detail to inspect acquisition, location, responsible person, photo, and history.',
      'Submit update or disposal requests when asset condition, location, value, or status changes.',
      'Approval flows differ by role and branch responsibility.',
    ],
    tags: ['assets', 'fixed asset', 'disposal', 'update', 'history'],
  },
  {
    category: 'Inventory',
    title: 'Stock Adjustment',
    icon: 'SlidersIcon',
    summary: 'Adjust item stock and reconcile physical counts through stock opname workflows.',
    flow: ['Requester', 'Lab Manager', 'Administrator'],
    steps: [
      'Use Item Stocks to inspect branch stock levels and movement history.',
      'Create stock opname when physical count needs reconciliation.',
      'Submit counted quantities and notes for approval by the configured role.',
      'Confirmed adjustment updates stock and creates traceable stock history.',
    ],
    tags: ['stock adjustment', 'stock opname', 'item stock', 'inventory'],
  },
]

export default {
  data() {
    return {
      isOpen: false,
      search: '',
      isSearchFocused: false,
      activeCategory: 'All',
      guides,
    }
  },
  computed: {
    categories() {
      return ['All', ...new Set(this.guides.map(item => item.category))]
    },
    filteredGuides() {
      const query = this.search.trim().toLowerCase()

      return this.guides.filter(item => {
        const matchesCategory = this.activeCategory === 'All' || item.category === this.activeCategory
        const haystack = [
          item.category,
          item.title,
          item.summary,
          item.steps.join(' '),
          item.tags.join(' '),
          (item.flow || []).join(' '),
        ].join(' ').toLowerCase()

        return matchesCategory && (!query || haystack.includes(query))
      })
    },
  },
  watch: {
    isOpen(value) {
      if (value) {
        this.$nextTick(() => {
          if (this.$refs.searchInput) this.$refs.searchInput.focus()
        })
      }
    },
  },
  methods: {
    clearSearch() {
      this.search = ''
      if (this.$refs.searchInput) this.$refs.searchInput.focus()
    },
  },
}
</script>

<style lang="scss" scoped>
.help-floating {
  position: fixed;
  right: 1.5rem;
  bottom: 1.5rem;
  z-index: 1050;
}

// Floating circular trigger carrying the GeoLIMS mark itself, rather than a
// generic "?" help pill - same treatment as a chat-widget launcher bubble.
.help-trigger {
  display: grid;
  width: 58px;
  height: 58px;
  padding: 0;
  place-items: center;
  color: #fff;
  border: 0;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 14px 30px rgba(31, 42, 68, 0.28), 0 0 0 3px rgba(43, 138, 126, 0.14);
  transition: transform 0.18s ease, box-shadow 0.18s ease;

  &:hover {
    transform: translateY(-2px) scale(1.04);
    box-shadow: 0 18px 40px rgba(31, 42, 68, 0.34), 0 0 0 4px rgba(43, 138, 126, 0.22);
  }

  &:active {
    transform: translateY(0) scale(0.98);
  }

  img {
    width: 68%;
    height: 68%;
    object-fit: contain;
  }

  svg {
    color: #1f2a44;
  }
}

.help-trigger-icon-enter-active,
.help-trigger-icon-leave-active {
  transition: opacity 0.14s ease, transform 0.14s ease;
}

.help-trigger-icon-enter,
.help-trigger-icon-leave-to {
  opacity: 0;
  transform: scale(0.7);
}

.help-panel {
  position: absolute;
  right: 0;
  bottom: 4.5rem;
  display: flex;
  flex-direction: column;
  width: min(460px, calc(100vw - 2rem));
  max-height: min(700px, calc(100vh - 7rem));
  overflow: hidden;
  border: 1px solid rgba(34, 41, 47, 0.08);
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 28px 64px rgba(24, 30, 48, 0.24);
}

.help-panel__header {
  display: flex;
  flex: 0 0 auto;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.1rem 1.1rem 0.9rem;
  color: #fff;
  background: linear-gradient(135deg, #1f2a44 0%, #255f85 56%, #2b8a7e 100%);

  span {
    display: block;
    color: rgba(255, 255, 255, 0.72);
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .help-panel__eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    padding: 0.15rem 0.5rem 0.15rem 0.4rem;
    color: #ffe9a8;
    border: 1px solid rgba(255, 233, 168, 0.35);
    border-radius: 999px;
    background: rgba(255, 233, 168, 0.12);
  }

  h2 {
    margin: 0.1rem 0 0.2rem;
    color: #fff;
    font-size: 1.3rem;
    font-weight: 800;
    letter-spacing: 0;
  }

  p {
    margin: 0;
    color: rgba(255, 255, 255, 0.78);
    font-size: 0.8rem;
  }
}

.help-icon-btn {
  display: grid;
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  place-items: center;
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 9px;
  background: rgba(255, 255, 255, 0.14);
  transition: background 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.24);
  }
}

.help-search {
  position: relative;
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 0.55rem;
  margin: 0.95rem 1.1rem 0.35rem;
  padding: 0.55rem 0.7rem;
  border: 1px solid #e7e7ef;
  border-radius: 12px;
  background: #f7f8fb;
  box-shadow: none;
  transition: border-color 0.16s ease, background 0.16s ease, box-shadow 0.16s ease;

  &:hover {
    border-color: #d7d9e6;
  }

  &--focused {
    border-color: rgba(43, 138, 126, 0.55);
    background: #fff;
    box-shadow: 0 0 0 3px rgba(43, 138, 126, 0.12);
  }
}

.help-search__icon {
  display: grid;
  flex: 0 0 auto;
  place-items: center;
  color: #2b8a7e;
}

.help-search__input {
  flex: 1 1 auto;
  min-width: 0;
  padding: 0;
  color: #1f2a44;
  border: 0;
  outline: none;
  background: transparent;
  font-size: 0.86rem;
  font-weight: 500;
  box-shadow: none;
  appearance: none;

  &::placeholder {
    color: #9a97ab;
    font-weight: 400;
  }

  &:focus {
    outline: none;
    box-shadow: none;
  }
}

.help-search__clear {
  display: grid;
  flex: 0 0 22px;
  width: 22px;
  height: 22px;
  place-items: center;
  color: #8f8ba3;
  border: 0;
  border-radius: 50%;
  background: rgba(143, 139, 163, 0.12);
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    color: #fff;
    background: #ea5455;
  }
}

.help-search__spark {
  display: grid;
  flex: 0 0 auto;
  place-items: center;
  color: #d3a53d;
  opacity: 0.85;
}

.help-tabs {
  display: flex;
  flex: 0 0 auto;
  gap: 0.4rem;
  overflow-x: auto;
  padding: 0.7rem 1.1rem 0.8rem;

  button {
    flex: 0 0 auto;
    padding: 0.42rem 0.78rem;
    color: #5e5873;
    border: 1px solid #ebe9f1;
    border-radius: 999px;
    background: #fff;
    font-size: 0.8rem;
    font-weight: 700;
    transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;

    &.active {
      color: #fff;
      border-color: #2b8a7e;
      background: #2b8a7e;
    }

    &:not(.active):hover {
      border-color: rgba(43, 138, 126, 0.4);
      color: #2b8a7e;
    }
  }
}

.help-list {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 0 1.1rem 1.1rem;
}

.help-card {
  padding: 1.05rem 0;
  border-top: 1px solid #f0eef6;

  &:first-child {
    border-top: 0;
    padding-top: 0.15rem;
  }
}

.help-card__title {
  display: flex;
  gap: 0.75rem;

  > span {
    display: grid;
    flex: 0 0 36px;
    width: 36px;
    height: 36px;
    place-items: center;
    color: #2b8a7e;
    border-radius: 10px;
    background: rgba(43, 138, 126, 0.1);
  }

  h3 {
    margin-bottom: 0.15rem;
    color: #3f3b57;
    font-size: 0.98rem;
    font-weight: 800;
    letter-spacing: 0;
  }

  p {
    margin-bottom: 0;
    color: #6e6b7b;
    font-size: 0.85rem;
    line-height: 1.4;
  }
}

// -----------------------------------------------------------------------
// Role-flow diagram: a horizontal (wrapping) chain of chips connected by
// arrows, one chip per approving role in order. The last node is styled
// distinctly ("Finished") so the end of the chain reads clearly even when
// it wraps across lines.
// -----------------------------------------------------------------------
.help-flow {
  margin: 0.8rem 0 0;
  padding: 0.7rem 0.75rem;
  border: 1px solid rgba(43, 138, 126, 0.16);
  border-radius: 10px;
  background: linear-gradient(180deg, rgba(43, 138, 126, 0.05), rgba(43, 138, 126, 0.02));
}

.help-flow__track {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.3rem;
}

.help-flow__node {
  padding: 0.28rem 0.6rem;
  color: #1f2a44;
  border: 1px solid rgba(43, 138, 126, 0.28);
  border-radius: 999px;
  background: #fff;
  font-size: 0.74rem;
  font-weight: 700;
  line-height: 1.3;
  white-space: nowrap;

  &.is-finish {
    color: #fff;
    border-color: #2b8a7e;
    background: linear-gradient(135deg, #2b8a7e, #1f6f66);
  }
}

.help-flow__arrow {
  color: rgba(43, 138, 126, 0.55);
  font-size: 0.8rem;
  font-weight: 700;
  line-height: 1;
}

.help-flow__note {
  margin: 0.5rem 0 0;
  color: #6e6b7b;
  font-size: 0.72rem;
  font-style: italic;
  line-height: 1.4;
}

.help-card__steps {
  margin: 0.75rem 0 0;
  padding-left: 1.2rem;
  color: #5e5873;
  font-size: 0.86rem;

  li + li {
    margin-top: 0.35rem;
  }
}

.help-card__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-top: 0.8rem;

  span {
    padding: 0.2rem 0.5rem;
    color: #5e5873;
    border-radius: 999px;
    background: #f3f2f7;
    font-size: 0.7rem;
    font-weight: 700;
  }
}

.help-empty {
  display: grid;
  gap: 0.35rem;
  min-height: 190px;
  place-items: center;
  color: #6e6b7b;
  text-align: center;

  strong {
    color: #3f3b57;
  }

  span {
    max-width: 260px;
    font-size: 0.82rem;
  }
}

.help-panel-enter-active,
.help-panel-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.help-panel-enter,
.help-panel-leave-to {
  opacity: 0;
  transform: translateY(10px) scale(0.98);
}

@media (max-width: 575.98px) {
  .help-floating {
    right: 1rem;
    bottom: 1rem;
  }

  // On phones the panel was still sized/positioned like a desktop popover
  // (`width: min(460px, 100vw - 2rem)`, anchored via `position: absolute`
  // off the trigger button, height capped against `100vh` which mobile
  // browsers get wrong once the address bar is factored in) - it ended up
  // reading as oversized and slightly off-screen rather than a tidy sheet.
  // Pin it to the viewport directly instead, sized to leave a clear margin
  // on every side and a real safe max-height (`100dvh`, the viewport height
  // that actually accounts for mobile browser chrome, with a `vh` fallback
  // first for older browsers that don't support `dvh` at all).
  .help-panel {
    position: fixed;
    top: auto;
    right: 0.75rem;
    bottom: 4.75rem;
    left: 0.75rem;
    width: auto;
    max-height: calc(100vh - 6rem);
    max-height: calc(100dvh - 6rem);
  }

  .help-panel__header {
    padding: 0.9rem 0.9rem 0.7rem;

    h2 {
      font-size: 1.15rem;
    }
  }

  .help-search {
    margin: 0.75rem 0.9rem 0.25rem;
  }

  .help-tabs {
    padding: 0.6rem 0.9rem 0.7rem;
  }

  .help-list {
    padding: 0 0.9rem 0.9rem;
  }
}
</style>
